import { Box } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import UploadIcon from "@material-ui/icons/CloudUpload";
import { CompanySelect } from "components/inputs";
import { YesNoInput } from "components/inputs";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import { SplitOrdersType } from "entities/SplitOrdersType";
import React, { useEffect, useState } from "react";
import {
	Button,
	ReferenceInput,
	SelectInput,
	required,
	useNotify,
	useTranslate,
} from "react-admin";
import { Form, useFormState } from "react-final-form";
import { isInArray } from "utils/general";

import { httpClient } from "../../../api/httpClient";
import { MapperType } from "../../../entities/MapperType";
import { NotCustomerRoles } from "../../../entities/UserRole";
import { useNotifyError } from "../../../utils/notifiers/useNotifyError";
import { DocumentAddButton } from "../../fields/DocumentsField/AddButton";
import { DropPaper } from "../../fields/DocumentsField/DropPaper";
import { FileInfo } from "../../fields/DocumentsField/SimpleFileInfo";

const UploadDialog = ({ role, company_id, onDialogClose, onDialogOpen, setSharedData }) => {
	const notifyError = useNotifyError();
	const notify = useNotify();

	const [selectedFile, setSelectedFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [companyId, setCompanyId] = useState(company_id || null);
	const [configData, setConfigData] = useState(null);

	const uploadData = React.useCallback(
		(values) => {
			const data = new FormData();
			data.append("file", values.file);
			data.append("mapper_id", values.mapper_id);
			data.append("company_id", values.company_id);
			data.append("split_orders_type", values.split_orders_type);
			data.append("add_loading_sequence_to_ref", values.add_loading_sequence_to_ref);
			data.append("should_generate_pallets", values.should_generate_pallets);
			data.append("attach_source_doc", values.attach_source_doc);
			data.append("split_document_content", values.split_document_content);

			setUploading(true);
			httpClient("orders-upload-preview", { method: "POST", body: data })
				.then(({ json }) =>
					httpClient("orders-upload-validate", {
						method: "POST",
						body: JSON.stringify(json),
					})
				)
				.then(({ json }) => {
					const {
						company_id,
						mapper_type,
						order_items,
						order_import_id,
						split_orders_type,
						add_loading_sequence_to_ref,
						attach_source_doc,
						split_document_content,
						should_generate_pallets,
					} = json;
					setSharedData({
						company_id,
						mapper_type,
						data: order_items,
						order_import_id,
						split_orders_type,
						add_loading_sequence_to_ref,
						attach_source_doc,
						split_document_content,
						should_generate_pallets,
					});
					onDialogOpen();
				})
				.catch(notifyError)
				.finally(() => {
					setUploading(false);
				});
		},
		[setSharedData, onDialogOpen]
	);

	const handleOnSubmit = React.useCallback(
		(values) => {
			if (selectedFile) {
				uploadData({ ...values, file: selectedFile });
			} else {
				notify("File is required", "warning");
			}
		},
		[selectedFile, uploadData]
	);

	return (
		<Form
			onSubmit={handleOnSubmit}
			initialValues={{
				mapper_id: configData?.order_import_mapper_id,
				company_id: companyId,
				split_orders_type:
					configData?.order_import_split_orders_type?.value ?? SplitOrdersType.not_split,
				add_loading_sequence_to_ref:
					configData?.order_import_split_orders_type?.value === SplitOrdersType.not_split
						? false
						: configData?.is_order_import_add_loading_sequence_to_ref?.value ?? true,
				attach_source_doc: configData?.is_order_import_attach_source_doc?.value ?? true,
				should_generate_pallets: configData?.is_order_import_generate_pallets?.value ?? false,
				split_document_content: configData?.is_order_import_split_document_content?.value ?? true,
			}}
		>
			{(formProps) => (
				<UploadDialogForm
					formProps={formProps}
					role={role}
					companyId={companyId}
					setCompanyId={setCompanyId}
					onDialogClose={onDialogClose}
					selectedFile={selectedFile}
					setSelectedFile={setSelectedFile}
					uploading={uploading}
					configData={configData}
					setConfigData={setConfigData}
					notifyError={notifyError}
				/>
			)}
		</Form>
	);
};

const UploadDialogForm = ({
	formProps,
	role,
	companyId,
	setCompanyId,
	onDialogClose,
	selectedFile,
	setSelectedFile,
	uploading,
	configData,
	setConfigData,
	notifyError,
}) => {
	const t = useTranslate();
	const { values } = useFormState();
	const isNoneCustomerRole = role && isInArray(NotCustomerRoles, role);

	useEffect(() => {
		if (values.company_id) {
			setCompanyId(values.company_id);
			httpClient(`customer-companies/${values.company_id}`)
				.then(({ json }) => {
					if (json?.order_import_mapper_id) {
						setConfigData({ order_import_mapper_id: json.order_import_mapper_id });
					} else {
						setConfigData(null);
					}
				})
				.catch((error) => {
					notifyError(error);
				})
				.finally(() => {});
		} else {
			setCompanyId(null);
			setConfigData(null);
		}
	}, [values.company_id]);

	useEffect(() => {
		// setConfigData({ ...configData, order_import_mapper_id: values.mapper_id });
		if (values.mapper_id) {
			httpClient(`mappers/${values.mapper_id}`)
				.then(({ json }) => {
					const mapperConfig = json?.settings.reduce((acc, item) => {
						acc[item.key] = { value: item.value, is_show_on_import: item.is_show_on_import };
						return acc;
					}, {});
					setConfigData({
						...configData,
						...mapperConfig,
						order_import_mapper_id: values.mapper_id,
					});
				})
				.catch((error) => {
					notifyError(error);
				})
				.finally(() => {});
		}
	}, [values.mapper_id]);

	const onSplitOrderChange = (e) => {
		if (e.target.value === SplitOrdersType.not_split) {
			formProps.form.change("add_loading_sequence_to_ref", false);
			formProps.form.change("split_document_content", true);
		} else {
			formProps.form.change(
				"add_loading_sequence_to_ref",
				configData?.is_order_import_add_loading_sequence_to_ref?.value ?? true
			);
			formProps.form.change(
				"split_document_content",
				configData?.is_order_import_split_document_content?.value ?? true
			);
		}
	};
	const onAttachSourceDocChange = (e) => {
		if (!e.target.value) {
			formProps.form.change("split_document_content", true);
			formProps.form.change("should_generate_pallets", false);
		}
	};

	return (
		<Dialog open maxWidth="sm" onClose={onDialogClose} fullWidth>
			<DialogTitle>{t("osfc.dialogs.orders-import-upload.title")}</DialogTitle>
			<DialogContent>
				{isNoneCustomerRole && (
					<CompanySelect
						fullWidth
						source="company_id"
						onChange={(e) => {
							if (e?.target?.value) {
								formProps.form.change("mapper_id", null);
							}
						}}
						validate={[required()]}
					/>
				)}

				<ReferenceInput
					fullWidth
					reference="mappers"
					source="mapper_id"
					label={t("osfc.dialogs.orders-import-upload.fields.mapper_id")}
					filter={{ type: MapperType.order_import, company_id: companyId, is_upload_order: true }}
					validate={[required()]}
					disabled={!companyId}
				>
					<SelectInput optionText="name" />
				</ReferenceInput>

				{configData?.order_import_split_orders_type?.is_show_on_import && (
					<SelectEnumInput
						fullWidth
						source="split_orders_type"
						label={t("osfc.dialogs.orders-import-upload.fields.split_orders_type")}
						key="split_orders_type"
						resettable
						enumName="split-orders-type"
						enumObj={SplitOrdersType}
						onChange={onSplitOrderChange}
					/>
				)}

				{configData?.is_order_import_add_loading_sequence_to_ref?.is_show_on_import &&
					values.split_orders_type !== SplitOrdersType.not_split && (
						<YesNoInput
							fullWidth
							source="add_loading_sequence_to_ref"
							label={t("osfc.dialogs.orders-import-upload.fields.add_loading_sequence_to_ref")}
							defaultValue={true}
							helperText={t(
								"osfc.dialogs.orders-import-upload.fields.helper_text.add_loading_sequence_to_ref"
							)}
						/>
					)}

				{configData?.is_order_import_attach_source_doc?.is_show_on_import && (
					<YesNoInput
						fullWidth
						source="attach_source_doc"
						label={t("osfc.dialogs.orders-import-upload.fields.attach_source_doc")}
						defaultValue={true}
						helperText={t("osfc.dialogs.orders-import-upload.fields.helper_text.attach_source_doc")}
						onChange={onAttachSourceDocChange}
					/>
				)}

				{configData?.is_order_import_split_document_content?.is_show_on_import &&
					values.attach_source_doc &&
					values.split_orders_type !== SplitOrdersType.not_split && (
						<YesNoInput
							fullWidth
							source="split_document_content"
							label={t("osfc.dialogs.orders-import-upload.fields.split_document_content")}
							defaultValue={true}
							helperText={t(
								"osfc.dialogs.orders-import-upload.fields.helper_text.split_document_content"
							)}
						/>
					)}
				{configData?.is_order_import_generate_pallets?.is_show_on_import &&
					values.attach_source_doc && (
						<YesNoInput
							fullWidth
							source="should_generate_pallets"
							label={t("osfc.dialogs.orders-import-upload.fields.should_generate_pallets")}
							defaultValue={false}
							helperText={t(
								"osfc.dialogs.orders-import-upload.fields.helper_text.should_generate_pallets"
							)}
						/>
					)}

				<DropPaper processDocument={setSelectedFile} elevation={0} variant="outlined">
					<Box
						p={2}
						pb={selectedFile ? 0 : 5}
						display="flex"
						justifyContent="space-between"
						alignItems="center"
					>
						<DocumentAddButton
							uploading={false}
							uploadFile={setSelectedFile}
							acceptTypes=".xls,.xlsx"
						/>
					</Box>
					{selectedFile && (
						<Box p={2}>
							<FileInfo file={selectedFile} setFile={setSelectedFile} />
						</Box>
					)}
				</DropPaper>
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					disabled={uploading}
					label={t("ra.action.cancel")}
					onClick={onDialogClose}
					color="secondary"
				/>
				<Button
					variant="contained"
					label={t("osfc.buttons.upload")}
					onClick={formProps.handleSubmit}
					color="primary"
					startIcon={
						uploading ? (
							<CircularProgress size={16} thickness={5} color="inherit" />
						) : (
							<UploadIcon />
						)
					}
				/>
			</DialogActions>
		</Dialog>
	);
};

export default UploadDialog;
